import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  Clock,
  Calendar,
  TrendingUp,
  TrendingDown,
  Users,
  FileText,
  DollarSign,
  Download,
  Filter,
  ChevronDown
} from 'lucide-react';
import { api } from '../../features/auth/authStore';
import toast from 'react-hot-toast';

interface TimeAnalytics {
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  totalEntries: number;
  averageSessionLength: number;
  productivity: {
    thisWeek: number;
    lastWeek: number;
    change: number;
  };
  topTasks: Array<{
    taskId: string;
    taskTitle: string;
    totalMinutes: number;
    entries: number;
  }>;
  dailyBreakdown: Array<{
    date: string;
    totalMinutes: number;
    billableMinutes: number;
    entries: number;
  }>;
  hourlyDistribution: Array<{
    hour: number;
    minutes: number;
  }>;
}

interface TimeTrackingAnalyticsProps {
  userRole: string;
  userId?: string;
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
  onDateRangeChange: (range: { startDate: Date; endDate: Date }) => void;
  className?: string;
}

export const TimeTrackingAnalytics: React.FC<TimeTrackingAnalyticsProps> = ({
  userRole,
  userId,
  dateRange,
  onDateRangeChange,
  className = ''
}) => {
  const [analytics, setAnalytics] = useState<TimeAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, selectedPeriod, userId]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
        period: selectedPeriod
      });

      if (userId) {
        params.append('userId', userId);
      }

      const response = await api.get(`/time-entries/analytics?${params}`);
      if (response.data.success) {
        setAnalytics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching time analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatHours = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  const getProductivityTrend = () => {
    if (!analytics) return { icon: TrendingUp, color: 'text-gray-500', text: 'No data' };
    
    const change = analytics.productivity.change;
    if (change > 0) {
      return { icon: TrendingUp, color: 'text-green-500', text: `+${change.toFixed(1)}%` };
    } else if (change < 0) {
      return { icon: TrendingDown, color: 'text-red-500', text: `${change.toFixed(1)}%` };
    } else {
      return { icon: TrendingUp, color: 'text-gray-500', text: '0%' };
    }
  };

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    
    const now = new Date();
    let startDate = new Date();
    
    switch (period) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
    }
    
    onDateRangeChange({ startDate, endDate: now });
  };

  const exportData = async () => {
    try {
      const params = new URLSearchParams({
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
        format: 'csv'
      });

      if (userId) {
        params.append('userId', userId);
      }

      const response = await api.get(`/time-entries/export?${params}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `time-tracking-report-${selectedPeriod}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export report');
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="text-center py-8">
          <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No analytics data available</p>
        </div>
      </div>
    );
  }

  const productivityTrend = getProductivityTrend();
  const ProductivityIcon = productivityTrend.icon;

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Time Tracking Analytics</h2>
            <p className="text-sm text-gray-600 mt-1">
              {dateRange.startDate.toLocaleDateString()} - {dateRange.endDate.toLocaleDateString()}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Filter className="w-4 h-4 mr-2" />
                {selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)}
                <ChevronDown className="w-4 h-4 ml-2" />
              </button>
              
              {showFilters && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                  <div className="py-1">
                    {['today', 'week', 'month', 'quarter', 'year'].map((period) => (
                      <button
                        key={period}
                        onClick={() => {
                          handlePeriodChange(period);
                          setShowFilters(false);
                        }}
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                          selectedPeriod === period ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                        }`}
                      >
                        {period.charAt(0).toUpperCase() + period.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <button
              onClick={exportData}
              className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Total Hours</p>
                <p className="text-2xl font-bold text-blue-900">{formatHours(analytics.totalHours * 60)}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Billable Hours</p>
                <p className="text-2xl font-bold text-green-900">{formatHours(analytics.billableHours * 60)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600">Time Entries</p>
                <p className="text-2xl font-bold text-purple-900">{analytics.totalEntries}</p>
              </div>
              <FileText className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600">Productivity</p>
                <div className="flex items-center">
                  <p className="text-2xl font-bold text-orange-900 mr-2">
                    {analytics.productivity.thisWeek.toFixed(1)}h
                  </p>
                  <div className={`flex items-center ${productivityTrend.color}`}>
                    <ProductivityIcon className="w-4 h-4 mr-1" />
                    <span className="text-sm font-medium">{productivityTrend.text}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Tasks */}
        {analytics.topTasks.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Tasks by Time</h3>
            <div className="space-y-3">
              {analytics.topTasks.slice(0, 5).map((task, index) => (
                <div key={task.taskId} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-sm font-medium text-primary-700">{index + 1}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{task.taskTitle}</p>
                      <p className="text-sm text-gray-600">{task.entries} entries</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{formatHours(task.totalMinutes)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Daily Breakdown Chart Placeholder */}
        {analytics.dailyBreakdown.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Breakdown</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 text-center">
                Chart visualization would be implemented here with a charting library like Chart.js or Recharts
              </p>
              <div className="mt-4 space-y-2">
                {analytics.dailyBreakdown.slice(-7).map((day) => (
                  <div key={day.date} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{new Date(day.date).toLocaleDateString()}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-blue-600">Total: {formatHours(day.totalMinutes)}</span>
                      <span className="text-green-600">Billable: {formatHours(day.billableMinutes)}</span>
                      <span className="text-gray-500">{day.entries} entries</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
